import type { RefObject } from "react";
import { useLayoutEffect } from "react";

const SCREEN_EDGE_SPACING = 5;

type Position = {
  top: number;
  left: number;
};

type PositionCalculationParams = {
  triggerRect: DOMRect;
  containerWidth: number;
  containerHeight: number;
  offset: number;
  viewportWidth: number;
  viewportHeight: number;
};

export function useTooltipContainerPosition(
  triggerRef: RefObject<HTMLElement | null>,
  containerRef: RefObject<HTMLElement | null>,
  open: boolean,
  deps: React.DependencyList = []
) {
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !containerRef.current) return;

    const trigger = triggerRef.current;
    const container = containerRef.current;
    const offsetValue = getComputedStyle(trigger).getPropertyValue("--tooltip-offset");
    const offset = offsetValue ? parseInt(offsetValue) : 0;

    const updatePosition = () => {
      const params = getPositionInformation(trigger, container, offset);
      const isFlipped = shouldFlip(params);
      const position = calculateFinalPosition(params, isFlipped);

      container.style.position = "fixed";
      container.style.top = `${position.top}px`;
      container.style.left = `${position.left}px`;
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, ...deps]);
}

function getPositionInformation(
  trigger: HTMLElement,
  container: HTMLElement,
  offset: number
): PositionCalculationParams {
  const triggerRect = trigger.getBoundingClientRect();

  return {
    triggerRect,
    containerWidth: container.offsetWidth,
    containerHeight: container.offsetHeight,
    offset,
    viewportWidth: window.visualViewport?.width || window.innerWidth,
    viewportHeight: window.visualViewport?.height || window.innerHeight,
  };
}

function shouldFlip(params: PositionCalculationParams): boolean {
  const { triggerRect, containerHeight, offset, viewportHeight } = params;

  const topPosition = triggerRect.top - containerHeight - offset;

  const isClippedTop = topPosition <= 0;
  const isOverflowBottom = isTooltipOverflowingBottom(triggerRect, containerHeight, offset, viewportHeight);
  const canFitAboveViewport = topPosition >= 0;

  if (isClippedTop && !isOverflowBottom) {
    return false;
  }

  if (isOverflowBottom && canFitAboveViewport) {
    return true;
  }

  if (isClippedTop && isOverflowBottom) {
    const spaceAbove = triggerRect.top;
    const spaceBelow = viewportHeight - triggerRect.bottom;

    if (spaceBelow > spaceAbove) {
      return false;
    } else {
      return true;
    }
  }

  return true;
}

function isTooltipOverflowingBottom(
  triggerRect: DOMRect,
  containerHeight: number,
  offset: number,
  viewportHeight: number
): boolean {
  const bottomPosition = triggerRect.bottom + containerHeight + offset;
  return bottomPosition > viewportHeight - SCREEN_EDGE_SPACING;
}

function calculateFinalPosition(params: PositionCalculationParams, placement: boolean): Position {
  const { triggerRect, containerWidth, containerHeight, offset, viewportWidth } = params;

  // Vertical position
  const top = placement ? triggerRect.top - containerHeight - offset : triggerRect.bottom + offset;

  // Horizontal position
  const centeredLeft = triggerRect.left + triggerRect.width / 2 - containerWidth / 2;
  const left = constrainToViewportHorizontally(centeredLeft, containerWidth, viewportWidth);

  return { top, left };
}

function constrainToViewportHorizontally(left: number, containerWidth: number, viewportWidth: number) {
  if (left < SCREEN_EDGE_SPACING) {
    return SCREEN_EDGE_SPACING;
  }

  if (left + containerWidth > viewportWidth - SCREEN_EDGE_SPACING) {
    return Math.max(SCREEN_EDGE_SPACING, viewportWidth - containerWidth - SCREEN_EDGE_SPACING);
  }

  return left;
}
